/**
 * WorkflowPicker — Shared workflow selection component.
 *
 * Used in 3 contexts:
 * 1. Workflow Viewer toolbar dropdown (filter='all', showBadges=true)
 * 2. Automation sidebar Process Tab (filter='executable', showBadges=false)
 * 3. Automation sidebar Workflows Tab (filter='all', showBadges=true, showFilterChips=true)
 *
 * Features:
 * - Search with a "create with this name" option
 * - Category-grouped expandable sections
 * - Optional readiness badges
 * - Optional filter chips (All/Ready)
 * - "New workflow" creates a blank workflow directly — curated templates live in the marketplace
 * - Optional "none" row that clears the selection
 */

import { notify } from '@affine/component';
import { useConnectivityStatus } from '@affine/core/components/hooks/use-connectivity-status';
import { useI18n } from '@affine/i18n';
import {
  ArrowRightSmallIcon,
  CloseIcon,
  DoneIcon,
  PlusIcon,
  SearchIcon,
} from '@blocksuite/icons/rc';
import clsx from 'clsx';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { WorkflowInfo } from '../services/workflow';
import { usePersistedUiState } from '../utils/use-persisted-ui-state';
import * as styles from './workflow-picker.css';

// =============================================================================
// Types
// =============================================================================

export interface WorkflowPickerProps {
  /** All available workflows (unfiltered — component applies its own filters) */
  workflows: WorkflowInfo[];
  /** Currently selected workflow ID */
  selectedId?: string | null;
  /** Callback when a workflow is selected */
  onSelect: (workflow: WorkflowInfo) => void;
  /** Callback to create a new blank workflow. If undefined, create is disabled. */
  onCreate?: (name: string) => Promise<WorkflowInfo | null>;
  /** Keep create controls visible but block the action with offline feedback. */
  createUnavailable?: boolean;
  /** Callback to delete a workflow. If undefined, delete buttons are hidden. */
  onDelete?: (workflowId: string, workflowName: string) => void;
  /** Clears the selection: with a selected workflow a "none" row is offered. */
  onClear?: () => void;
  /** Below the list, after the create action (e.g. a notice). */
  footer?: ReactNode;

  /** Which workflows to show: 'all' or 'executable' (ready only) */
  filter?: 'all' | 'executable';
  /** Show readiness badges on each item */
  showBadges?: boolean;
  /** Show All/Ready filter chip bar */
  showFilterChips?: boolean;
  /** Whether the list is loading */
  isLoading?: boolean;
  /** Auto-focus the search input on mount */
  autoFocusSearch?: boolean;
  /** Footer info text */
  footerText?: string;
}

interface WorkflowPickerPersistedState {
  searchQuery: string;
  filterPublished: boolean;
  expandedCategories: string[];
}

/** Groups the workflows without a category; shown under a translated name. */
const UNCATEGORIZED = 'Uncategorized';

const WORKFLOW_PICKER_STATE_KEY_PREFIX = 'streamlain.workflow.picker-state.v1';

const WORKFLOW_PICKER_STATE_FALLBACK: WorkflowPickerPersistedState = {
  searchQuery: '',
  filterPublished: false,
  expandedCategories: [],
};

function normalizeWorkflowPickerState(
  value: unknown
): WorkflowPickerPersistedState {
  const input =
    value && typeof value === 'object'
      ? (value as Partial<WorkflowPickerPersistedState>)
      : {};

  return {
    searchQuery: typeof input.searchQuery === 'string' ? input.searchQuery : '',
    filterPublished:
      typeof input.filterPublished === 'boolean'
        ? input.filterPublished
        : false,
    expandedCategories: Array.isArray(input.expandedCategories)
      ? input.expandedCategories.filter(
          (category): category is string => typeof category === 'string'
        )
      : [],
  };
}

// =============================================================================
// Component
// =============================================================================

export function WorkflowPicker({
  workflows,
  selectedId,
  onSelect,
  onCreate,
  createUnavailable = false,
  onDelete,
  onClear,
  footer,
  filter = 'all',
  showBadges = false,
  showFilterChips = false,
  isLoading = false,
  autoFocusSearch = false,
  footerText,
}: WorkflowPickerProps) {
  const t = useI18n();
  const connectivityStatus = useConnectivityStatus();
  const searchRef = useRef<HTMLInputElement>(null);
  const [persistedState, setPersistedState] =
    usePersistedUiState<WorkflowPickerPersistedState>(
      `${WORKFLOW_PICKER_STATE_KEY_PREFIX}:${filter}:${showFilterChips ? 'panel' : 'dropdown'}`,
      WORKFLOW_PICKER_STATE_FALLBACK,
      normalizeWorkflowPickerState
    );
  const [searchQuery, setSearchQuery] = useState(persistedState.searchQuery);
  const [creating, setCreating] = useState(false);
  const isCreateUnavailable = connectivityStatus.offline || createUnavailable;
  const offlineCreateTitle =
    t['com.streamlain.streamlain.workflow.create.offline-title']();
  const offlineCreateMessage =
    t['com.streamlain.streamlain.workflow.create.offline-message']();
  const showOfflineCreateToast = useCallback(() => {
    notify.warning({
      title: offlineCreateTitle,
      message: offlineCreateMessage,
    });
  }, [offlineCreateMessage, offlineCreateTitle]);

  const newWorkflowName =
    t['com.streamlain.streamlain.workflow.page.create-new']();
  const searchLabel =
    t['com.streamlain.settings.workspace.workflows.search.placeholder']();

  // Filter chips (only when showFilterChips=true)
  const [filterPublished, setFilterPublished] = useState(
    persistedState.filterPublished
  );

  // Category group collapse state — manual toggles tracked separately
  const [manualExpandedCategories, setManualExpandedCategories] = useState<
    Set<string>
  >(() => new Set(persistedState.expandedCategories));

  const updateSearchQuery = useCallback(
    (value: string) => {
      setSearchQuery(value);
      setPersistedState(prev => ({ ...prev, searchQuery: value }));
    },
    [setPersistedState]
  );

  const updateFilterPublished = useCallback(
    (value: boolean) => {
      setFilterPublished(value);
      setPersistedState(prev => ({ ...prev, filterPublished: value }));
    },
    [setPersistedState]
  );

  useEffect(() => {
    if (autoFocusSearch) {
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [autoFocusSearch]);

  // Step 1: Pre-filter by mode (executable = published only)
  const baseFiltered = useMemo(() => {
    if (filter === 'executable') {
      return workflows.filter(w => w.active);
    }
    return workflows;
  }, [workflows, filter]);

  // Step 2: Apply search + chip filters
  const filtered = useMemo(() => {
    let result = baseFiltered;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        w =>
          w.name.toLowerCase().includes(q) ||
          w.categories?.some(c => c.name.toLowerCase().includes(q))
      );
    }

    if (filterPublished) {
      result = result.filter(w => w.active);
    }

    return result;
  }, [baseFiltered, searchQuery, filterPublished]);

  // Group by categories
  const categoryGroups = useMemo(() => {
    const groups = new Map<string, WorkflowInfo[]>();

    for (const wf of filtered) {
      const catNames = wf.categories?.length
        ? wf.categories.map(c => c.name)
        : [UNCATEGORIZED];
      for (const catName of catNames) {
        const existing = groups.get(catName) ?? [];
        groups.set(catName, [...existing, wf]);
      }
    }

    return Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === UNCATEGORIZED) return 1;
      if (b === UNCATEGORIZED) return -1;
      return a.localeCompare(b);
    });
  }, [filtered]);

  // Auto-expand groups with matches when search ≥ 3 chars
  const expandedCategories = useMemo(() => {
    if (searchQuery.trim().length >= 3) {
      return new Set(categoryGroups.map(([cat]) => cat));
    }
    return manualExpandedCategories;
  }, [searchQuery, categoryGroups, manualExpandedCategories]);

  // Categories start collapsed — user expands manually

  const toggleCategory = useCallback(
    (cat: string) => {
      setManualExpandedCategories(prev => {
        const next = new Set(prev);
        if (next.has(cat)) next.delete(cat);
        else next.add(cat);
        setPersistedState(state => ({
          ...state,
          expandedCategories: [...next],
        }));
        return next;
      });
    },
    [setPersistedState]
  );

  // Create a blank workflow directly — search text is used as the name if provided.
  const handleCreateClick = useCallback(
    async (name: string) => {
      if (!onCreate || creating) return;
      if (isCreateUnavailable) {
        showOfflineCreateToast();
        return;
      }
      setCreating(true);
      try {
        const workflow = await onCreate(name || newWorkflowName);
        if (workflow) {
          onSelect(workflow);
          updateSearchQuery('');
        }
      } catch (err) {
        notify.error({
          title: t['com.streamlain.streamlain.workflow.request-failed'](),
          message: err instanceof Error ? err.message : String(err),
        });
      } finally {
        setCreating(false);
      }
    },
    [
      t,
      onCreate,
      creating,
      isCreateUnavailable,
      showOfflineCreateToast,
      newWorkflowName,
      onSelect,
      updateSearchQuery,
    ]
  );

  const handleAllFilter = useCallback(() => {
    updateFilterPublished(false);
  }, [updateFilterPublished]);

  // Determine if "Create" row should show
  const showCreateRow = onCreate && searchQuery.trim().length > 0;

  const statusOf = (workflow: WorkflowInfo) =>
    workflow.active
      ? t['com.streamlain.streamlain.workflow.page.active']()
      : t['com.streamlain.streamlain.workflow.page.inactive']();

  /** A workflow's row: the pinned current one and the rows of a category. */
  const workflowRow = (workflow: WorkflowInfo, pinned: boolean) => {
    const selected = workflow.id === selectedId;
    return (
      <div
        key={workflow.id}
        className={clsx(
          styles.workflowItem,
          selected && styles.workflowItemSelected,
          pinned && styles.currentWorkflowRow
        )}
        data-pinned={pinned || undefined}
        aria-current={selected || undefined}
        onClick={pinned ? undefined : () => onSelect(workflow)}
      >
        <div
          className={clsx(
            styles.statusDot,
            workflow.active ? styles.statusDotActive : styles.statusDotInactive
          )}
          title={statusOf(workflow)}
        />
        <div className={styles.workflowInfo}>
          <div className={styles.workflowName}>{workflow.name}</div>
          {showBadges && (
            <div className={styles.workflowMeta}>{statusOf(workflow)}</div>
          )}
        </div>
        {showBadges && (
          <div className={styles.badgeContainer}>
            <span
              className={
                workflow.active
                  ? styles.badgePublished
                  : styles.badgeUnpublished
              }
            >
              {statusOf(workflow)}
            </span>
          </div>
        )}
        {selected && <DoneIcon className={styles.selectedCheck} />}
        {onDelete && (
          <button
            type="button"
            className={styles.deleteBtn}
            onClick={e => {
              e.stopPropagation();
              onDelete(workflow.id, workflow.name);
            }}
            aria-label={t['com.streamlain.streamlain.workflow.picker.delete']({
              name: workflow.name,
            })}
            title={t['com.streamlain.streamlain.workflow.picker.delete']({
              name: workflow.name,
            })}
          >
            <CloseIcon />
          </button>
        )}
      </div>
    );
  };

  const current = selectedId
    ? workflows.find(w => w.id === selectedId)
    : undefined;

  const canClear = Boolean(selectedId && onClear);

  return (
    <div
      className={clsx(
        styles.container,
        showFilterChips ? styles.containerPanel : styles.containerDropdown
      )}
    >
      <div className={styles.searchContainer}>
        <label className={styles.searchField}>
          <SearchIcon className={styles.searchIcon} />
          <input
            ref={searchRef}
            type="text"
            className={styles.searchInput}
            aria-label={searchLabel}
            placeholder={searchLabel}
            value={searchQuery}
            onChange={e => updateSearchQuery(e.target.value)}
            onKeyDown={e => e.stopPropagation()}
          />
        </label>
      </div>

      {/* Filter Chips (only in panel mode / Workflows Tab) */}
      {showFilterChips && (
        <div className={styles.filterBar}>
          <button
            className={clsx(
              styles.filterChip,
              !filterPublished && styles.filterChipActive
            )}
            onClick={handleAllFilter}
          >
            {t['com.streamlain.streamlain.workflow.gallery.category-all']()}
          </button>
          <button
            className={clsx(
              styles.filterChip,
              filterPublished && styles.filterChipActive
            )}
            onClick={() => updateFilterPublished(!filterPublished)}
          >
            {t['com.streamlain.streamlain.workflow.page.active']()}
          </button>
        </div>
      )}

      {/* The selection: "none" clears it, the current workflow stays in view. */}
      {(current || canClear) && (
        <div className={styles.selection}>
          {canClear && (
            <button
              type="button"
              className={styles.actionRow}
              onClick={onClear}
              data-testid="workflow-picker-clear"
            >
              <CloseIcon className={styles.actionIcon} />
              {t['com.streamlain.streamlain.common.none']()}
            </button>
          )}
          {current && workflowRow(current, true)}
        </div>
      )}

      {/* Workflow List */}
      <div className={styles.list}>
        {showCreateRow && (
          <button
            type="button"
            className={styles.actionRow}
            data-accent
            onClick={() => void handleCreateClick(searchQuery.trim())}
            aria-disabled={isCreateUnavailable || creating}
            title={isCreateUnavailable ? offlineCreateMessage : undefined}
          >
            <PlusIcon className={styles.actionIcon} />
            {t['com.streamlain.streamlain.workflow.picker.create-named']({
              name: searchQuery.trim(),
            })}
          </button>
        )}

        {isLoading && baseFiltered.length === 0 ? (
          <>
            {[1, 2, 3].map(i => (
              <div key={i} className={styles.skeleton}>
                <div className={styles.skeletonDot} />
                <div className={styles.skeletonText}>
                  <div
                    className={styles.skeletonLine}
                    style={{ width: '70%' }}
                  />
                  <div
                    className={styles.skeletonLine}
                    style={{ width: '40%', height: '8px' }}
                  />
                </div>
              </div>
            ))}
          </>
        ) : filtered.length === 0 && !showCreateRow ? (
          <div className={styles.emptyState}>
            <span>
              {searchQuery
                ? t[
                    'com.streamlain.streamlain.workflow.page.empty.no-match.title'
                  ]()
                : t[
                    'com.streamlain.streamlain.workflow.page.empty.no-workflows.title'
                  ]()}
            </span>
            <span className={styles.emptySubtext}>
              {searchQuery
                ? t[
                    'com.streamlain.streamlain.workflow.page.empty.no-match.hint'
                  ]()
                : filter === 'executable'
                  ? t['com.streamlain.streamlain.workflow.picker.none-ready']()
                  : t[
                      'com.streamlain.streamlain.workflow.page.empty.no-workflows.hint'
                    ]()}
            </span>
          </div>
        ) : (
          categoryGroups.map(([category, groupWorkflows]) => {
            const isExpanded = expandedCategories.has(category);
            return (
              <div key={category}>
                <button
                  type="button"
                  className={styles.categoryGroupHeader}
                  aria-expanded={isExpanded}
                  onClick={() => toggleCategory(category)}
                >
                  <ArrowRightSmallIcon
                    className={styles.categoryGroupChevron}
                    data-expanded={isExpanded}
                  />
                  <span className={styles.categoryGroupName}>
                    {category === UNCATEGORIZED
                      ? t[
                          'com.streamlain.streamlain.workflow.picker.uncategorized'
                        ]()
                      : category}
                  </span>
                  <span className={styles.categoryGroupCount}>
                    {groupWorkflows.length}
                  </span>
                </button>
                {isExpanded && (
                  <div className={styles.categoryGroupItems}>
                    {groupWorkflows.map(workflow =>
                      workflowRow(workflow, false)
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {(onCreate || footer || footerText) && (
        <div className={styles.footer}>
          {onCreate && (
            <button
              type="button"
              className={styles.actionRow}
              onClick={() => void handleCreateClick('')}
              disabled={creating}
              aria-disabled={isCreateUnavailable || creating}
              title={isCreateUnavailable ? offlineCreateMessage : undefined}
              data-testid="workflow-picker-create"
            >
              <PlusIcon className={styles.actionIcon} />
              {creating
                ? t['com.streamlain.streamlain.workflow.picker.creating']()
                : newWorkflowName}
            </button>
          )}
          {footer}
          {footerText && <div className={styles.footerInfo}>{footerText}</div>}
        </div>
      )}
    </div>
  );
}
